import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Wine, Package, Pencil, Trash2, Search, AlertCircle } from "lucide-react";
import VendorProductForm from "./VendorProductForm";

/**
 * Vendor catalog page. Renders only items matching `kind`.
 * @param {"wine"|"product"} kind
 */
export default function VendorCatalog({ kind }) {
    const isWine = kind === "wine";
    const WINE_CATS = ["Vinho Tinto", "Vinho Branco", "Rosé", "Espumante", "Vinho do Porto", "Madeira", "Verde", "Moscatel"];
    const [list, setList] = useState([]);
    const [producer, setProducer] = useState(null);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [q, setQ] = useState("");

    async function load() {
        const [{ data }, prodRes] = await Promise.all([
            api.get("/vendor/products"),
            api.get("/vendor/me").catch(() => ({ data: null })),
        ]);
        if (prodRes?.data) setProducer(prodRes.data);
        const filtered = data.filter((p) => {
            if (p.kind === kind) return true;
            if (!p.kind) return isWine ? WINE_CATS.some((c) => p.category === c) : !WINE_CATS.some((c) => p.category === c);
            return false;
        });
        setList(filtered);
    }
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

    async function del(p) {
        if (!window.confirm(`Eliminar "${p.name}"?`)) return;
        await api.delete(`/products/${p.id}`);
        toast.success("Eliminado");
        load();
    }

    const filtered = q
        ? list.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()) || (p.category || "").toLowerCase().includes(q.toLowerCase()))
        : list;

    const Icon = isWine ? Wine : Package;
    const title = isWine ? "Vinhos" : "Produtos Artesanais";
    const subtitle = isWine ? "Catálogo de vinhos da sua quinta" : "Compotas, azeites, queijos, enchidos e outras maravilhas";

    return (
        <section className="space-y-6" data-testid={`vendor-catalog-${kind}`}>
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <span className="label-overline text-wine/60 inline-flex items-center gap-1.5">
                        <Icon className="h-3 w-3" /> Catálogo
                    </span>
                    <h1 className="font-heading text-3xl text-wine-dark">{title}</h1>
                    <p className="text-sm text-wine-dark/60 mt-1">{subtitle}</p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-wine inline-flex items-center gap-2" data-testid="new-product-btn">
                    <Plus className="h-4 w-4" /> {isWine ? "Novo vinho" : "Novo produto"}
                </button>
            </div>

            {/* Search */}
            <div className="bg-white rounded-md border border-gold-soft/30 p-3 flex items-center gap-3">
                <Search className="h-4 w-4 text-wine-dark/40 ml-2" />
                <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={isWine ? "Procurar por nome ou tipo…" : "Procurar por nome ou categoria…"}
                    className="flex-1 bg-transparent text-sm focus:outline-none"
                    data-testid="search-catalog"
                />
                <span className="text-xs label-overline text-wine-dark/50">{filtered.length} resultados</span>
            </div>

            {/* List */}
            {filtered.length === 0 ? (
                <div className="bg-white border border-gold-soft/30 rounded-md p-12 text-center">
                    <Icon className="h-10 w-10 text-wine/30 mx-auto mb-3" strokeWidth={1.2} />
                    <p className="font-heading text-xl text-wine-dark">Ainda sem {isWine ? "vinhos" : "produtos"}</p>
                    <p className="text-sm text-wine-dark/55 mt-1">Comece por adicionar o seu primeiro {isWine ? "vinho" : "produto artesanal"}.</p>
                    <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-wine text-xs mt-5 inline-flex items-center gap-2">
                        <Plus className="h-3.5 w-3.5" /> Adicionar
                    </button>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filtered.map((p) => <ProductCard key={p.id} p={p} isWine={isWine} onEdit={() => { setEditing(p); setShowForm(true); }} onDelete={() => del(p)} />)}
                </div>
            )}

            {showForm && (
                <VendorProductForm
                    kind={kind}
                    initial={editing}
                    producer={producer}
                    onClose={() => setShowForm(false)}
                    onSaved={() => { setShowForm(false); load(); }}
                />
            )}
        </section>
    );
}

function ProductCard({ p, isWine, onEdit, onDelete }) {
    const img = p.images?.[0];
    const fullUrl = img && img.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;
    const producerPrice = p.producer_price != null ? p.producer_price : p.price * 0.85;
    const commission = p.price - producerPrice;

    return (
        <div className="bg-white border border-gold-soft/30 rounded-md overflow-hidden shadow-sm" data-testid={`product-card-${p.id}`}>
            <div className="relative aspect-[4/3] bg-cream-warm">
                {fullUrl ? (
                    <img src={fullUrl} alt={p.name} className="w-full h-full object-cover" />
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-wine/30">
                        {isWine ? <Wine className="h-10 w-10" /> : <Package className="h-10 w-10" />}
                    </div>
                )}
                <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                    {!p.approved && (
                        <span className="bg-gold-soft text-wine-dark text-[9px] label-overline px-2 py-0.5 rounded">A aguardar</span>
                    )}
                    {p.limited_edition && (
                        <span className="bg-wine text-cream text-[9px] label-overline px-2 py-0.5 rounded">Limitada</span>
                    )}
                    {p.packaging?.fragile && (
                        <span className="bg-cream text-destructive text-[9px] label-overline px-2 py-0.5 rounded inline-flex items-center gap-1">
                            <AlertCircle className="h-2.5 w-2.5" /> Frágil
                        </span>
                    )}
                </div>
            </div>
            <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <h3 className="font-heading text-lg text-wine-dark leading-tight truncate">{p.name}</h3>
                        <p className="text-xs text-wine-dark/55 mt-0.5">{p.category} · {p.region}</p>
                    </div>
                    <span className={`text-[10px] label-overline ${p.stock === 0 ? "text-destructive" : p.stock <= 5 ? "text-gold-deep" : "text-vineyard"}`}>
                        Stock {p.stock}
                    </span>
                </div>

                {isWine && p.wine?.vintage_year && (
                    <p className="text-xs text-wine-dark/65 mt-1">
                        {p.wine.vintage_year} · {p.wine.alcohol_pct ? `${p.wine.alcohol_pct}% vol` : ""} {p.wine.classification ? `· ${p.wine.classification}` : ""}
                    </p>
                )}

                <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-wine-dark/5">
                    <div>
                        <span className="label-overline text-vineyard text-[9px]">Produtor</span>
                        <p className="text-sm text-vineyard font-semibold">€{producerPrice.toFixed(2)}</p>
                    </div>
                    <div>
                        <span className="label-overline text-gold-deep text-[9px]">Comissão</span>
                        <p className="text-sm text-gold-deep">€{commission.toFixed(2)}</p>
                    </div>
                    <div>
                        <span className="label-overline text-wine text-[9px]">Venda</span>
                        <p className="font-heading text-base text-wine-dark">€{p.price.toFixed(2)}</p>
                    </div>
                </div>

                <div className="flex gap-2 mt-3 pt-3 border-t border-wine-dark/5">
                    <button onClick={onEdit} className="flex-1 text-xs label-overline text-wine hover:text-gold-deep inline-flex items-center justify-center gap-1 py-1.5" data-testid={`edit-${p.id}`}>
                        <Pencil className="h-3 w-3" /> Editar
                    </button>
                    <button onClick={onDelete} className="text-xs label-overline text-destructive/70 hover:text-destructive inline-flex items-center gap-1 py-1.5 px-2" data-testid={`delete-${p.id}`}>
                        <Trash2 className="h-3 w-3" />
                    </button>
                </div>
            </div>
        </div>
    );
}
